import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Share2, Inbox, Send, ExternalLink, Trash2, Eye, FileText, Target, User, Clock, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SharedPlansSection({ refreshKey }) {
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandSent, setExpandSent] = useState(false);

  const fetchSharedPlans = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/shared-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReceived(res.data.received || []);
      setSent(res.data.sent || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSharedPlans();
  }, [refreshKey]);

  const revokeShare = async (shareId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/shared-plans/${shareId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Share revoked");
      fetchSharedPlans();
    } catch (err) {
      toast.error("Failed to revoke share");
    }
  };

  if (loading) return null;
  if (received.length === 0 && sent.length === 0) return null;

  return (
    <div className="mb-8" data-testid="shared-plans-section">
      {/* Received plans */}
      {received.length > 0 && (
        <div className="mb-6">
          <h3 className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-3">
            <Inbox className="w-4 h-4 text-blue-600" /> Shared With Me ({received.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {received.map((share) => (
              <SharedPlanCard key={share.share_id} share={share} type="received" />
            ))}
          </div>
        </div>
      )}

      {/* Sent plans */}
      {sent.length > 0 && (
        <div>
          <button
            data-testid="toggle-sent-shares"
            onClick={() => setExpandSent(!expandSent)}
            className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-3 hover:text-emerald-700 transition-colors"
          >
            <Send className="w-4 h-4 text-emerald-600" /> My Shared Plans ({sent.length})
            {expandSent ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {expandSent && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sent.map((share) => (
                <SharedPlanCard key={share.share_id} share={share} type="sent" onRevoke={revokeShare} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SharedPlanCard({ share, type, onRevoke }) {
  const snap = share.plan_snapshot || {};
  const progress = share.progress_data;

  return (
    <Card className="border-0 shadow-md bg-gradient-to-br from-white to-blue-50/30 hover:shadow-lg transition-all" data-testid={`shared-plan-${share.share_id}`}>
      <CardHeader className="p-3 pb-1">
        <div className="flex items-start justify-between">
          <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100">
            <FileText className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex items-center gap-1">
            {type === "received" && (
              <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">
                From {share.sharer_name}
              </span>
            )}
            {type === "sent" && share.recipient_email && (
              <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">
                To {share.recipient_email}
              </span>
            )}
            {type === "sent" && !share.recipient_email && (
              <span className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-semibold">
                Link shared
              </span>
            )}
          </div>
        </div>
        <CardTitle className="text-sm leading-tight text-gray-900 mt-1">
          {snap.title || "Shared Plan"}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-2">
          <Target className="w-3 h-3" />
          <span>{snap.duration_weeks || "?"}w plan</span>
          {snap.difficulty_level && (
            <>
              <span className="text-gray-300">|</span>
              <span className="capitalize">{snap.difficulty_level}</span>
            </>
          )}
        </div>

        {/* Progress if included */}
        {progress && (
          <div className="bg-blue-50 rounded-lg px-2.5 py-1.5 mb-2">
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-blue-700 font-medium">Progress</span>
              <span className="text-blue-800 font-bold">{progress.completion_pct}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-1 mt-1">
              <div className="bg-blue-600 h-1 rounded-full transition-all" style={{ width: `${progress.completion_pct}%` }} />
            </div>
          </div>
        )}

        {share.message && (
          <p className="text-[10px] text-gray-500 italic mb-2 line-clamp-2">"{share.message}"</p>
        )}

        <div className="flex items-center justify-between">
          <span className="text-[9px] text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(share.created_at).toLocaleDateString()}
          </span>
          <div className="flex gap-1">
            <Button
              data-testid={`view-share-${share.share_id}`}
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-blue-600 hover:bg-blue-100"
              onClick={() => window.open(`/shared-plan/${share.share_id}`, "_blank")}
            >
              <Eye className="w-3.5 h-3.5" />
            </Button>
            {type === "sent" && onRevoke && (
              <Button
                data-testid={`revoke-share-${share.share_id}`}
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-red-600 hover:bg-red-100"
                onClick={() => onRevoke(share.share_id)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
